import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { sortPropertiesParentFirst } from "@/lib/pnl-columns";

interface PropertyLike {
  id: number;
  parentPropertyId?: number | null;
  status?: string | null;
  address?: string | null;
  name?: string | null;
}

interface PropertyFilterSelectProps {
  value: string;
  onValueChange: (v: string) => void;
  properties: PropertyLike[];
  testId?: string;
  placeholder?: string;
  className?: string;
}

/**
 * Shared property-filter Select used by both IncomeTab and ExpensesTab.
 * Shows parent properties with "+N units" and children indented with "└ ".
 * Selecting "all" passes "" to onValueChange; selecting a property passes its
 * string id. Parent→child rollup (expanding selection to include child units)
 * is the caller's responsibility in their filteredRows/displayedRows memo.
 */
export function PropertyFilterSelect({
  value,
  onValueChange,
  properties,
  testId = "select-property-filter",
  placeholder = "All Properties",
  className = "w-44 text-sm",
}: PropertyFilterSelectProps) {
  const selectable = properties.filter((p) => p.status !== "archive");
  return (
    <SearchableSelect
      value={value || "all"}
      onValueChange={(v) => onValueChange(v === "all" ? "" : v)}
      placeholder={placeholder}
      searchPlaceholder="Search properties..."
      emptyText="No properties match."
      testId={testId}
      className={className}
      options={[
        { value: "all", label: placeholder },
        ...sortPropertiesParentFirst(selectable as any).map((p: any) => {
          const childCount = properties.filter(
            (c) => c.parentPropertyId === p.id,
          ).length;
          const isChild = !!p.parentPropertyId;
          const label = p.address || p.name || `Property #${p.id}`;
          return {
            value: String(p.id),
            label: `${isChild ? `  \u2514 ${label}` : label}${childCount > 0 ? ` (+${childCount} units)` : ""}`,
            keywords: `${p.address ?? ""} ${p.name ?? ""}`,
          };
        }),
      ]}
    />
  );
}

interface CategoryOption {
  value: string;
  label: string;
}

interface CategoryFilterSelectProps {
  value: string;
  onValueChange: (v: string) => void;
  categories: CategoryOption[];
  testId?: string;
  placeholder?: string;
  className?: string;
}

/**
 * Shared category-filter Select used by both IncomeTab and ExpensesTab.
 * Renders an "All Categories" sentinel plus the provided category list.
 * Selecting "all" passes "" to onValueChange; selecting a category passes its value string.
 */
export function CategoryFilterSelect({
  value,
  onValueChange,
  categories,
  testId = "select-category-filter",
  placeholder = "All Categories",
  className = "w-44 text-sm",
}: CategoryFilterSelectProps) {
  return (
    <Select
      value={value || "all"}
      onValueChange={(v) => onValueChange(v === "all" ? "" : v)}
    >
      <SelectTrigger className={className} data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {categories.map((c) => (
          <SelectItem key={c.value} value={c.value}>
            {c.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
